interface PaymentSelectionOptions {
  itemName: string;
  price: number;
  userBalance?: number;
}

class PaymentSelectionModalService {
  private resolveCallback: ((value: 'usdt_balance' | 'usdt_crypto' | null) => void) | null = null;
  private modalComponent: any = null;
  private options: PaymentSelectionOptions | null = null;

  setModalComponent(component: any) {
    this.modalComponent = component;
  }

  async showPaymentSelection(
    options: PaymentSelectionOptions,
  ): Promise<'usdt_balance' | 'usdt_crypto' | null> {
    return new Promise((resolve) => {
      this.resolveCallback = resolve;
      this.options = options;
      if (this.modalComponent) {
        this.modalComponent.open(options);
      }
    });
  }

  getOptions() {
    return this.options;
  }

  selectPaymentType(type: 'usdt_balance' | 'usdt_crypto') {
    if (this.resolveCallback) {
      this.resolveCallback(type);
      this.resolveCallback = null;
    }
    if (this.modalComponent) {
      this.modalComponent.close();
    }
  }

  close() {
    if (this.resolveCallback) {
      this.resolveCallback(null);
      this.resolveCallback = null;
    }
    if (this.modalComponent) {
      this.modalComponent.close();
    }
  }
}

export const paymentSelectionModalService = new PaymentSelectionModalService();

import { UIModal, UIButton } from 'shared/ui';
import styles from './payment-modal/style.module.scss';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import UsdtCoin from 'assets/icons/usdt.webp';
import { formatLargeNumber } from 'shared/utils';

export const UsdtPaymentSelectionModal = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<'usdt_balance' | 'usdt_crypto'>(
    'usdt_crypto',
  );
  const [options, setOptions] = useState<PaymentSelectionOptions>({
    itemName: '',
    price: 0,
    userBalance: 0,
  });

  useEffect(() => {
    paymentSelectionModalService.setModalComponent({
      open: (opts: PaymentSelectionOptions) => {
        setOptions(opts);
        setIsOpen(true);
        // Set default selection based on balance
        if ((opts.userBalance || 0) >= opts.price) {
          setSelectedPayment('usdt_balance');
        } else {
          setSelectedPayment('usdt_crypto');
        }
      },
      close: () => setIsOpen(false),
    });
  }, []);

  const handleConfirm = () => {
    paymentSelectionModalService.selectPaymentType(selectedPayment);
  };

  const handleClose = () => {
    paymentSelectionModalService.close();
  };

  const isBalanceDisabled = !options.userBalance || options.userBalance < options.price;

  return (
    <UIModal visible={isOpen} onClose={handleClose}>
      <div className={styles.usdtPaymentModal}>
        <span className={styles.modalHeader}>
          {options.itemName || t('v2.market.payment_selection.payment_for_item')}
        </span>

        <div className={styles.priceDisplay}>
          <span className={styles.price}>
            {formatLargeNumber(options.price, 2)}
          </span>
          <img src={UsdtCoin} alt="USDT" className={styles.usdtIcon} />
          <span className={styles.currency}>
            USDT
          </span>
        </div>

        <span className={styles.selectPaymentText}>{t('v2.market.payment_selection.select_payment_method')}</span>

        <div className={styles.paymentOptions}>
          <button
            className={`${styles.paymentOption} ${selectedPayment === 'usdt_balance' ? styles.selected : ''} ${isBalanceDisabled ? styles.disabled : ''}`}
            onClick={() => !isBalanceDisabled && setSelectedPayment('usdt_balance')}
            disabled={isBalanceDisabled}
          >
            <div className={styles.optionContent}>
              <span className={styles.optionTitle}>{t('v2.market.payment_selection.usdt_game_balance')}</span>
              <div className={styles.balanceAmount}>
                <span className={styles.amount}>
                  {formatLargeNumber(options.userBalance || 0, 2)}
                </span>
                <img src={UsdtCoin} alt="USDT" className={styles.smallUsdtIcon} />
                <span className={styles.amount}>USDT</span>
              </div>
            </div>
            <div className={styles.radioButton}>
              <div className={styles.radioOuter}>
                <div className={styles.radioInner} />
              </div>
            </div>
          </button>

          <button
            className={`${styles.paymentOption} ${selectedPayment === 'usdt_crypto' ? styles.selected : ''}`}
            onClick={() => setSelectedPayment('usdt_crypto')}
          >
            <div className={styles.optionContent}>
              <span className={styles.optionTitle}>{t('v2.market.payment_selection.ton_wallet')}</span>
            </div>
            <div className={styles.radioButton}>
              <div className={styles.radioOuter}>
                <div className={styles.radioInner} />
              </div>
            </div>
          </button>
        </div>

        <UIButton variant="primary" className={styles.confirmButton} onClick={handleConfirm}>
          {t('v2.market.payment_selection.pay')}
        </UIButton>
      </div>
    </UIModal>
  );
};
